import * as XLSX from "xlsx";

export interface ImportColumn {
  header: string;
  field: string;
  required?: boolean;
  type: "text" | "int";
}

export const IMPORT_COLUMNS: ImportColumn[] = [
  { header: "姓名", field: "name", required: true, type: "text" },
  { header: "性别", field: "gender", required: true, type: "text" },
  { header: "年龄", field: "age", type: "int" },
  { header: "身高", field: "height", type: "int" },
  { header: "体重", field: "weight", type: "int" },
  { header: "学历", field: "education", type: "text" },
  { header: "婚姻状况", field: "marital_status", type: "text" },
  { header: "子女情况", field: "children_status", type: "text" },
  { header: "现居城市", field: "residence", type: "text" },
  { header: "籍贯", field: "hometown", type: "text" },
  { header: "职业", field: "occupation", type: "text" },
  { header: "月收入", field: "income", type: "text" },
  { header: "住房情况", field: "house_status", type: "text" },
  { header: "车辆情况", field: "car_status", type: "text" },
  { header: "手机号", field: "phone", required: true, type: "text" },
  { header: "微信号", field: "wechat", type: "text" },
  { header: "兴趣爱好", field: "hobbies", type: "text" },
  { header: "自我介绍", field: "self_intro", type: "text" },
  { header: "期望年龄起", field: "partner_age_min", type: "int" },
  { header: "期望年龄止", field: "partner_age_max", type: "int" },
  { header: "择偶要求", field: "partner_requirements", type: "text" },
];

export const HEADER_ALIASES: Record<string, string> = {
  姓名: "name",
  名字: "name",
  会员姓名: "name",
  性别: "gender",
  年龄: "age",
  身高: "height",
  体重: "weight",
  学历: "education",
  婚姻状况: "marital_status",
  婚姻: "marital_status",
  子女情况: "children_status",
  子女: "children_status",
  现居城市: "residence",
  现居地: "residence",
  城市: "residence",
  籍贯: "hometown",
  职业: "occupation",
  月收入: "income",
  收入: "income",
  住房情况: "house_status",
  住房: "house_status",
  车辆情况: "car_status",
  车辆: "car_status",
  车况: "car_status",
  手机号: "phone",
  手机号码: "phone",
  联系电话: "phone",
  电话: "phone",
  微信号: "wechat",
  微信: "wechat",
  兴趣爱好: "hobbies",
  爱好: "hobbies",
  自我介绍: "self_intro",
  介绍: "self_intro",
  期望年龄起: "partner_age_min",
  期望对方年龄起: "partner_age_min",
  年龄起: "partner_age_min",
  期望年龄止: "partner_age_max",
  期望对方年龄止: "partner_age_max",
  年龄止: "partner_age_max",
  择偶要求: "partner_requirements",
  择偶条件: "partner_requirements",
};

export interface ParsedRow {
  data: Record<string, string | number | null>;
  errors: string[];
}

export interface ParseResult {
  rows: ParsedRow[];
  validCount: number;
  errorCount: number;
  skippedHeaders: string[];
}

function normalizeHeader(header: string): string {
  return String(header).replace(/[^\u4e00-\u9fa5]/g, "");
}

const ALIAS_LOOKUP: Record<string, string> = Object.entries(HEADER_ALIASES).reduce(
  (acc, [key, value]) => {
    acc[normalizeHeader(key)] = value;
    return acc;
  },
  {} as Record<string, string>,
);

export function normalizeGender(value: string): string {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const lower = raw.toLowerCase();
  if (["男", "先生", "男士", "male", "m"].includes(lower) || lower.includes("男")) return "男";
  if (["女", "女士", "小姐", "female", "f"].includes(lower) || lower.includes("女")) return "女";
  return "";
}

function toInt(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const num = parseInt(String(value).replace(/[^\d]/g, ""), 10);
  return Number.isNaN(num) ? null : num;
}

export function buildRows(raw: Record<string, unknown>[]): ParseResult {
  const rows: ParsedRow[] = [];
  const skipped = new Set<string>();
  let validCount = 0;
  let errorCount = 0;

  raw.forEach((record) => {
    const mapped: Record<string, string | number | null> = {};
    let hasValue = false;

    Object.entries(record).forEach(([key, value]) => {
      const field = ALIAS_LOOKUP[normalizeHeader(key)];
      if (!field) {
        if (normalizeHeader(key)) skipped.add(key);
        return;
      }
      const str = String(value ?? "").trim();
      if (str) hasValue = true;
      mapped[field] = str;
    });

    if (!hasValue) return;

    const errors: string[] = [];

    IMPORT_COLUMNS.forEach((col) => {
      const current = mapped[col.field];
      if (col.type === "int") {
        mapped[col.field] = toInt(current);
      } else {
        const str = current === null || current === undefined ? "" : String(current).trim();
        mapped[col.field] = str ? str : null;
      }
    });

    const name = String(mapped.name ?? "").trim();
    const phone = String(mapped.phone ?? "").trim();
    const gender = normalizeGender(String(mapped.gender ?? ""));

    if (!name) errors.push("缺少姓名");
    if (!phone) errors.push("缺少手机号");
    if (!gender) errors.push("性别需为男或女");
    mapped.gender = gender || null;

    const row: ParsedRow = { data: mapped, errors };
    if (errors.length) errorCount += 1;
    else validCount += 1;
    rows.push(row);
  });

  return { rows, validCount, errorCount, skippedHeaders: Array.from(skipped) };
}

export function parseWorkbookFile(file: File): Promise<ParseResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const buffer = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(buffer, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        if (!firstSheetName) {
          reject(new Error("文件中没有可读取的工作表"));
          return;
        }
        const sheet = workbook.Sheets[firstSheetName];
        const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
          defval: "",
          raw: false,
        });
        if (raw.length === 0) {
          reject(new Error("表格内容为空，请检查是否填写了数据"));
          return;
        }
        resolve(buildRows(raw));
      } catch {
        reject(new Error("文件解析失败，请确认是有效的 Excel 或 CSV 文件"));
      }
    };
    reader.onerror = () => reject(new Error("文件读取失败，请重试"));
    reader.readAsArrayBuffer(file);
  });
}

export const TEMPLATE_HEADERS = IMPORT_COLUMNS.map((col) =>
  col.required ? `${col.header}*` : col.header,
);

export function downloadTemplate(): void {
  const headSheet = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS]);
  headSheet["!cols"] = TEMPLATE_HEADERS.map(() => ({ wch: 16 }));

  const guideRows: (string | number)[][] = [
    ["字段", "是否必填", "说明 / 可选值"],
    ...IMPORT_COLUMNS.map((col) => [
      col.header,
      col.required ? "必填" : "选填",
      col.type === "int" ? "请输入数字" : col.field === "gender" ? "男 / 女" : "文本内容",
    ]),
  ];
  const guideSheet = XLSX.utils.aoa_to_sheet(guideRows);
  guideSheet["!cols"] = [{ wch: 14 }, { wch: 10 }, { wch: 30 }];

  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, headSheet, "会员资料");
  XLSX.utils.book_append_sheet(book, guideSheet, "填写说明");
  XLSX.writeFile(book, "锦伴金秋-会员导入模板.xlsx");
}